import React, { useEffect, useMemo, useState } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import AutocompleteTagFilter from "../AutocompleteTagFilter";
import Select from "../Select";

// shadcn
import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import { Label } from "components/ui/label";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogTrigger,
} from "components/ui/dialog";
import { Badge } from "components/ui/badge";
import { ArrowUp, ArrowDown } from "lucide-react";
import { Separator } from "components/ui/separator";

// icons
import { SlidersHorizontal, RotateCcw } from "lucide-react";

const simpleInputFields = [
    { label: "שם", field: "name" },
    { label: "פלאפון", field: "phone" },
    { label: "מוצר", field: "product" },
    { label: "הערות", field: "comments" },
];

const statusOptions = [
    { value: "all", label: "כל הסטטוסים", color: "transparent" },
    { value: "pending", label: "ממתין", color: "#A8D5BA" },
    { value: "inProgress", label: "בתהליך", color: "#FFD59E" },
    { value: "done", label: "הסתיים", color: "#F7A8A8" },
];

const sortFields = [
    { key: "", label: "ללא" },
    { key: "name", label: "שם" },
    { key: "product", label: "מוצר" },
    { key: "date", label: "תאריך" },
];

const sortOrderOptions = [
    { value: "asc", label: "עולה", color: "transparent" },
    { value: "desc", label: "יורד", color: "transparent" },
];

const emptyFilters = {
    name: "",
    phone: "",
    status: "",
    product: "",
    comments: "",
    startDate: null,
    endDate: null,
    tags: [],
};

const isTruthy = (v) => (Array.isArray(v) ? v.length > 0 : !!v);

const SortBadges = ({ sort, setSort }) => {
  const activeField =
    sort.sortField && sort.sortField !== "all" ? sort.sortField : "";
  const activeOrder =
    sort.sortOrder && sort.sortOrder !== "all" ? sort.sortOrder : "";

  const onFieldClick = (fieldKey) => {
    setSort((prev) => {
      const prevField = prev.sortField && prev.sortField !== "all" ? prev.sortField : "";
      const prevOrder = prev.sortOrder && prev.sortOrder !== "all" ? prev.sortOrder : "";

      // Toggle direction if clicking active field
      if (fieldKey === prevField) {
        return {
          ...prev,
          sortOrder: prevOrder === "asc" ? "desc" : "asc",
        };
      }

      // New field → activate with default direction
      return {
        sortField: fieldKey,
        sortOrder: fieldKey === "date" ? "desc" : "asc", // sensible default
      };
    });
  };

  const clearSort = () => setSort({ sortField: "", sortOrder: "" });

  return (
    <div className="space-y-2" dir="rtl">
      <div className="flex items-center justify-between">
        <Label className="text-sm">מיון לפי</Label>

        {activeField && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={clearSort}
            className="h-8 px-2 text-muted-foreground"
          >
            ללא מיון
          </Button>
        )}
      </div>

      <div className="flex flex-wrap gap-2">
        {sortFields
          .filter((f) => f.key) // exclude "ללא"
          .map((f) => {
            const isActive = f.key === activeField;

            return (
              <button
                key={f.key}
                type="button"
                onClick={() => onFieldClick(f.key)}
                className="focus:outline-none"
                aria-pressed={isActive}
              >
                <Badge
                  variant={isActive ? "default" : "secondary"}
                  className={[
                    "rounded-full px-3 py-1 text-sm transition-colors",
                    "inline-flex items-center gap-1.5",
                    isActive
                      ? "bg-blue-600 text-white hover:bg-blue-700"
                      : "bg-gray-100 text-gray-800 hover:bg-gray-200",
                  ].join(" ")}
                >
                  <span>{f.label}</span>

                  {isActive ? (
                    activeOrder === "asc" ? (
                      <ArrowUp className="h-3.5 w-3.5" />
                    ) : (
                      <ArrowDown className="h-3.5 w-3.5" />
                    )
                  ) : (
                    <ArrowDown className="h-3.5 w-3.5 opacity-25" />
                  )}
                </Badge>
              </button>
            );
          })}
      </div>
    </div>
  );
};



const OrdersQueryDialog = ({
    onApply,
    initialFilters = {},
    initialSort = { sortField: "", sortOrder: "" },
}) => {
    const [open, setOpen] = useState(false);

    const [filters, setFilters] = useState({ ...emptyFilters, ...initialFilters });
    const [sort, setSort] = useState({
        sortField: initialSort.sortField || "",
        sortOrder: initialSort.sortOrder || "",
    });

    // Important: keep draft synced when opening (prevents stale UI)
    useEffect(() => {
        if (!open) return;
        setFilters({ ...emptyFilters, ...initialFilters });
        setSort({
            sortField: initialSort.sortField || "",
            sortOrder: initialSort.sortOrder || "",
        });
    }, [open, initialFilters, initialSort]);

    const activeCount = useMemo(() => {
        const fActive =
            ["name", "phone", "status", "product", "comments", "startDate", "endDate"].filter((k) =>
                isTruthy(filters[k])
            ).length + (filters.tags?.length ? 1 : 0);

        const sActive = sort.sortField ? 1 : 0;
        return fActive + sActive;
    }, [filters, sort]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFilters((prev) => ({ ...prev, [name]: value }));
    };

    const handleDateChange = (dates) => {
        const [start, end] = dates;
        setFilters((prev) => ({ ...prev, startDate: start, endDate: end }));
    };

    const handleTagChange = (selectedTags) => {
        setFilters((prev) => ({ ...prev, tags: selectedTags.map((t) => ({ ...t })) }));
    };

    const resetAll = () => {
        const clearedFilters = { ...emptyFilters };
        const clearedSort = { sortField: "", sortOrder: "" };
        setFilters(clearedFilters);
        setSort(clearedSort);
        onApply?.({}, clearedSort);
    };

    const apply = () => {
        onApply?.(filters, sort);
        setOpen(false);
    };

    const isFilterActive = (field) => isTruthy(filters[field]);

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <Button variant="secondary" className="gap-2">
                    <SlidersHorizontal className="h-4 w-4" />
                    סינון ומיון
                    {activeCount > 0 && (
                        <span className="rounded-md bg-background px-2 py-0.5 text-xs font-semibold">
                            {activeCount}
                        </span>
                    )}
                </Button>
            </DialogTrigger>

            <DialogContent
                dir="rtl"
                className=" flex flex-col
                            w-[95vw] max-w-lg
                            h-[90dvh] sm:h-auto sm:max-h-[85vh]
                            p-0"
            >
                {/* Header */}
                <div className="shrink-0 p-6 pb-3 border-b">
                    <DialogHeader>
                        <DialogTitle>סינון ומיון הזמנות</DialogTitle>
                        <DialogDescription>בחר מסננים ומיון.</DialogDescription>
                    </DialogHeader>
                </div>

                {/* Body (only scroll area) */}
                <div className="min-h-0 flex-1 overflow-y-auto px-6 py-4">
                    {/* FILTERS */}
                    <div className="space-y-3">
                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                            {simpleInputFields.map((f) => (
                                <div key={f.field} className="space-y-1">
                                    <Label htmlFor={f.field}>{f.label}</Label>
                                    <Input
                                        id={f.field}
                                        name={f.field}
                                        value={filters[f.field]}
                                        onChange={handleInputChange}
                                        className={isFilterActive(f.field) ? "bg-accent/40" : ""}
                                        placeholder={f.label}
                                    />
                                </div>
                            ))}

                            <div className="space-y-1">
                                <Label>סטטוס</Label>
                                <Select
                                    options={statusOptions}
                                    initialValue={filters.status}
                                    onOptionChange={(v) => setFilters((prev) => ({ ...prev, status: v }))}
                                />
                            </div>

                            <div className="space-y-1">
                                <Label>טווח תאריכים</Label>
                                <DatePicker
                                    selected={filters.startDate}
                                    onChange={handleDateChange}
                                    startDate={filters.startDate}
                                    endDate={filters.endDate}
                                    selectsRange
                                    isClearable
                                    placeholderText="בחר טווח תאריכים"
                                    // Key: prevents popper/resize loops inside dialogs
                                    withPortal
                                    className={`w-full rounded-md border bg-background px-3 py-2 text-sm ring-offset-background ${isFilterActive("startDate") || isFilterActive("endDate") ? "bg-accent/40" : ""
                                        }`}
                                />
                            </div>

                            <div className="space-y-1 sm:col-span-2">
                                <Label>תגיות</Label>
                                <AutocompleteTagFilter
                                    selectedTags={filters.tags}
                                    setSelectedTags={handleTagChange}
                                />
                            </div>
                        </div>
                    </div>

                    <Separator className="my-4" />

                    {/* SORT */}
                    <SortBadges sort={sort} setSort={setSort} />
                </div>

                {/* Footer (always visible, no sticky needed) */}
                <div className="shrink-0 border-t bg-background p-4">
                    <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
                        <Button type="button" variant="ghost" onClick={resetAll} className="gap-2">
                            <RotateCcw className="h-4 w-4" />
                            נקה הכל
                        </Button>
                        <Button type="button" onClick={apply} className="min-w-28">
                            החל
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default OrdersQueryDialog;
